import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, QueryList } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, FormGroupDirective, UntypedFormBuilder, Validators } from '@angular/forms';
import { toggleEditMode, getExpertMode, getMotorrad } from '../../../../shared/utils';
import { merge, of, ReplaySubject, Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { CalculationService } from '../../../service/calculation.service';
import { PopupService } from '../../../service/popup.service';
import { MessageService } from '../../../service/message.service';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DateService } from '../../../service/date.service';
import { CustomErrorStateMatcher, CustomValidator } from 'src/shared/validation';
import { ILookupValue } from '../../../models/lookupvalues';
import { LookupService } from '../../../service/lookup.service';
import * as moment from 'moment';
import { RisikoKfzComponent } from '../risikokfz/risikokfz.component';
import { IKfzKasko } from '../../../models/kfzkasko';
import { ErrorMessageType } from '../../../../shared/enum';
import { IKfzRisiko } from '../../../models/kfzrisiko';
import { IEuroTaxResult } from '../../../models/eurotaxresult';

export enum enumKaskoInfoKeys {
  'OEAMTCVOLLKASKOINFO' = '1',
  'OEAMTCTEILKASKOINFO' = '2',
  'MRADVOLLKASKOSELBSTBEHALTINFO' = '3',
  'MRADTEILKASKOSELBSTBEHALTINFO' = '4',
}

@Component({
  selector: 'app-kaskokfz',
  templateUrl: './kaskokfz.component.html',
  styleUrls: ['./kaskokfz.component.scss']
})

export class KaskoKfzComponent implements OnInit, OnChanges {

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  risikoKfzComponent: RisikoKfzComponent;

  @Input()
  kaskoKfzComponents: QueryList<KaskoKfzComponent>;

  @Input()
  beginndatum: UntypedFormControl;

  @Input()
  selected = false;

  @Input()
  complete = false;

  @Input()
  idprefix: string;

  @Input()
  bmStufenKasko: ReplaySubject<Array<ILookupValue>>;

  @Input()
  fahrzeugarten: ReplaySubject<Array<ILookupValue>>;

  @Input()
  kaskoKfzComponent: KaskoKfzComponent;

  @Output()
  parameterChangedEvent = new EventEmitter<void>();

  kaskoVarianten = new ReplaySubject<Array<ILookupValue>>();
  selbstbehaltVarianten = new ReplaySubject<Array<ILookupValue>>();
  selbstbehalthoehen = new ReplaySubject<Array<number>>();

  expertMode: boolean;
  motorrad: boolean;

  kaskoVariantenSubscription: Subscription;
  kaskoVarianteDisabled = false;

  matcher = new CustomErrorStateMatcher();

  eurotaxListenpreis: Subscription;
  eurotax = false;
  preselectSbVariante: ILookupValue = null;
  selbstbehaltForKaskoInfo = new Map<string, number>();

  constructor(private route: ActivatedRoute, private calculationService: CalculationService, private translateService: TranslateService,
    private dateService: DateService, private popupService: PopupService, public lookupService: LookupService, private messageService: MessageService) {
    this.expertMode = getExpertMode(this.route);
    this.motorrad = getMotorrad(this.route);
  }

  static createFormGroup(fb: UntypedFormBuilder, motorrad: boolean, expertMode: boolean) {
    return fb.group({
      kaskoVariante: new UntypedFormControl('', Validators.required),
      listenpreis: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.min(1),
        motorrad ? Validators.max(30000) : Validators.max(expertMode ? 120000 : 100000),
        CustomValidator.listenSonderSummeValidierung(motorrad, expertMode)
      ]),
      sonderausstattung: new UntypedFormControl('', [
        Validators.pattern('^[0-9]*$'),
        motorrad ? Validators.max(15000) : Validators.max(50000),
        CustomValidator.listenSonderSummeValidierung(motorrad, expertMode)
      ]),
      fahrzeugZustand: new UntypedFormControl('', Validators.required),
      selbstbehaltVariante: new UntypedFormControl('', Validators.required),
      neuwertentschaedigung: new UntypedFormControl('', Validators.required),
      leasingklausel: new UntypedFormControl('', Validators.required),
      evorsorgepaket: new UntypedFormControl('', Validators.required),
      erhoehungSelbstbehalt: new UntypedFormControl('', Validators.required),
      leasingFahrzeug: new UntypedFormControl('', Validators.required),
      bmStufeKasko: new UntypedFormControl(''),
      vorsteuerabzugsberechtigt: new UntypedFormControl(''),
      sonderausstattungDetails: new UntypedFormControl(''),
      vinkulierungFahrzeug: new UntypedFormControl(''),
      vinkularglaeubiger: new UntypedFormControl('', Validators.required),
      leasingMotorrad: new UntypedFormControl(''),
      leasingOrt: new UntypedFormControl('')
    });
  }

  ngOnInit() {
    if (!this.complete) {
      this.onValueChanged();
      this.toggleEditMode();
    } else {
      this.vinkulierungFahrzeug.valueChanges.subscribe(() => {
        this.toggleEditMode();
      });
    }
  }


  get kaskoVariante() { return this.formGroup.get('kaskoVariante'); }
  get listenpreis() { return this.formGroup.get('listenpreis'); }
  get sonderausstattung() { return this.formGroup.get('sonderausstattung'); }
  get fahrzeugZustand() { return this.formGroup.get('fahrzeugZustand'); }
  get selbstbehaltVariante() { return this.formGroup.get('selbstbehaltVariante'); }
  get neuwertentschaedigung() { return this.formGroup.get('neuwertentschaedigung'); }
  get leasingFahrzeug() { return this.formGroup.get('leasingFahrzeug'); }
  get leasingklausel() { return this.formGroup.get('leasingklausel'); }
  get evorsorgepaket() { return this.formGroup.get('evorsorgepaket'); }
  get erhoehungSelbstbehalt() { return this.formGroup.get('erhoehungSelbstbehalt'); }
  get bmStufeKasko() { return this.formGroup.get('bmStufeKasko'); }
  get vorsteuerabzugsberechtigt() { return this.formGroup.get('vorsteuerabzugsberechtigt'); }
  get sonderausstattungDetails() { return this.formGroup.get('sonderausstattungDetails'); }
  get vinkulierungFahrzeug() { return this.formGroup.get('vinkulierungFahrzeug'); }
  get vinkularglaeubiger() { return this.formGroup.get('vinkularglaeubiger'); }
  get leasingMotorrad() { return this.formGroup.get('leasingMotorrad'); }
  get leasingOrt() { return this.formGroup.get('leasingOrt'); }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selected && changes.selected.previousValue === false && changes.selected.currentValue === true) {
      Promise.resolve().then(() => this.toggleEditMode());
    }
  }

  showKaskoversicherung() {
    return (this.risikoKfzComponent?.fahrzeugart.value &&
      this.risikoKfzComponent?.fahrzeugart.value.Key !== '1' &&   // Anhaenger
      this.risikoKfzComponent?.fahrzeugart.value.Key !== '23' &&  // Zugmaschinen
      this.risikoKfzComponent?.fahrzeugart.value.Key !== '5' &&   // Kleinmotorrad
      this.risikoKfzComponent?.fahrzeugart.value.Key !== '9' &&   // Motordreirad
      this.risikoKfzComponent?.fahrzeugart.value.Key !== '10'     // Motorfahrrad
      ? true : false) &&
      (this.risikoKfzComponent?.erstzulassungsdatum.valid &&
        moment(this.risikoKfzComponent?.erstzulassungsdatum.value).year() >= moment(this.beginndatum?.value).year() - this.maxAlterJahreTeilkasko);
  }

  get interesseKaskoversicherung() {
    return this.showKaskoversicherung() && this.kaskoVariante.value && this.kaskoVariante.value.Key !== null;
  }
  get showListenpreis() {
    return this.interesseKaskoversicherung;
  }
  get showSonderausstattung() {
    return this.interesseKaskoversicherung;
  }
  get showFahrzeugZustand() {
    return this.interesseKaskoversicherung;
  }
  get showNeuwertentschaedigung() {
    return !this.motorrad && this.interesseKaskoversicherung && this.kaskoVariante.value && this.kaskoVariante.value.Key === '1' &&
      moment(this.risikoKfzComponent?.erstzulassungsdatum.value).isSameOrAfter(moment(this.beginndatum?.value).subtract(3, 'years')) &&
      this.beginndatum?.value && (moment(this.beginndatum.value).isSameOrAfter(moment(new Date(2024, 3, 1))) &&
        this.risikoKfzComponent?.fahrzeugart.value && this.risikoKfzComponent?.antriebsart.value &&
        (this.risikoKfzComponent.antriebsart.value.Key !== '6' && (this.risikoKfzComponent.fahrzeugart.value.Key === '15' ||
          this.risikoKfzComponent.fahrzeugart.value.Key === '6' || this.risikoKfzComponent.fahrzeugart.value.Key === '8' ||
          this.risikoKfzComponent.fahrzeugart.value.Key === '7' || this.risikoKfzComponent.fahrzeugart.value.Key === '22') ||
          this.risikoKfzComponent.antriebsart.value.Key === '6' && (this.risikoKfzComponent.fahrzeugart.value.Key === '15' ||
            this.risikoKfzComponent.fahrzeugart.value.Key === '6')) ||
        moment(this.beginndatum.value).isBefore(moment(new Date(2024, 3, 1))) &&
        this.risikoKfzComponent?.fahrzeugart.value && (this.risikoKfzComponent.fahrzeugart.value.Key === '15' ||
          this.risikoKfzComponent.fahrzeugart.value.Key === '6' || this.risikoKfzComponent.fahrzeugart.value.Key === '8' ||
          this.risikoKfzComponent.fahrzeugart.value.Key === '7' || this.risikoKfzComponent.fahrzeugart.value.Key === '22') &&
        this.risikoKfzComponent?.antriebsart.value && this.risikoKfzComponent.antriebsart.value.Key !== '6') &&
      this.leasingFahrzeug.value === 'nein' ? true : false;
  }
  get showLeasingFahrzeug() {
    return !this.motorrad && this.interesseKaskoversicherung && (this.expertMode || this.kaskoVariante.value && this.kaskoVariante.value.Key !== '0') ? true : false;
  }
  get showLeasingklausel() {
    return !this.motorrad && this.interesseKaskoversicherung && this.kaskoVariante.value
      && this.kaskoVariante.value.Key === '1' && this.leasingFahrzeug.value === 'ja' ? true : false;
  }
  get showEVorsorgepaket() {
    return this.interesseKaskoversicherung && this.risikoKfzComponent.fahrzeugart.value && (this.risikoKfzComponent.fahrzeugart.value.Key === '6'
      || this.risikoKfzComponent.fahrzeugart.value.Key === '15') && this.risikoKfzComponent.antriebsart.value
      && (this.risikoKfzComponent.antriebsart.value.Key === '6' || this.risikoKfzComponent.antriebsart.value.Key === '13'
        || this.risikoKfzComponent.antriebsart.value.Key === '15' || this.risikoKfzComponent.antriebsart.value.Key === '17'
        || this.risikoKfzComponent.antriebsart.value.Key === '24' || this.risikoKfzComponent.antriebsart.value.Key === '27'
        || this.risikoKfzComponent.antriebsart.value.Key === '30') ? true : false;
  }
  get showErhoehungSelbstbehalt() {
    return this.showLeasingFahrzeug && this.kaskoVariante.value && this.kaskoVariante.value.Key !== '0' && !this.showOaemtcInfoForKaskoVariante ? true : false;
  }
  get showErhoehungSelbstbehaltHint() {
    return this.risikoKfzComponent?.fahrzeugart.value &&
      (this.risikoKfzComponent?.fahrzeugart.value.Key === '15' ||
        this.risikoKfzComponent?.fahrzeugart.value.Key === '6');
  }
  get showBmStufeKasko() {
    return !this.motorrad &&
      this.interesseKaskoversicherung &&
      this.expertMode &&
      this.kaskoIndex === this.firstIndex &&
      (this.risikoKfzComponent?.fahrzeugart.value && this.risikoKfzComponent?.fahrzeugart.value.Key !== '7' && this.risikoKfzComponent?.fahrzeugart.value.Key !== '8'
        && this.risikoKfzComponent?.fahrzeugart.value.Key !== '22') ? true : false;
  }
  get showVorsteuerabzugsberechtigt() {
    return this.interesseKaskoversicherung && this.risikoKfzComponent?.privatperson !== null &&
      !this.risikoKfzComponent?.privatperson && this.risikoKfzComponent?.fahrzeugart.value &&
      (this.risikoKfzComponent?.fahrzeugart.value.Key === '15' ||
        this.risikoKfzComponent?.fahrzeugart.value.Key === '7' || this.risikoKfzComponent?.fahrzeugart.value.Key === '8' ||
        this.risikoKfzComponent?.fahrzeugart.value.Key === '12' || this.risikoKfzComponent?.fahrzeugart.value.Key === '6' ||
        this.risikoKfzComponent?.fahrzeugart.value.Key === '9' || this.risikoKfzComponent?.fahrzeugart.value.Key === '10') ? true : false;
  }
  get showSonderausstattungDetails() {
    return this.interesseKaskoversicherung && this.complete;
  }
  get showVinkulierungFahrzeug() {
    return this.interesseKaskoversicherung && this.complete;
  }
  get showVinkularglaeubiger() {
    return this.vinkulierungFahrzeug.value === 'ja' && this.interesseKaskoversicherung && this.complete;
  }
  get showLeasingMotorrad() {
    return !this.expertMode && this.motorrad && this.interesseKaskoversicherung && this.complete;
  }
  get showLeasingOrt() {
    return !this.expertMode && (this.leasingFahrzeug.value === 'ja' || this.leasingMotorrad.value === 'ja') && this.interesseKaskoversicherung && this.complete;
  }

  get showVollkaskoHint() {
    return this.kaskoVariante.value && this.kaskoVariante.value.Key === '1';
  }

  get erhoehungSelbstbehaltInfo() {
    if (this.kaskoVariante.value && this.selbstbehaltVariante.value) {
      if (this.kaskoVariante.value.Key === '1' && this.erhoehungSelbstbehalt.value) {
        if (this.selbstbehaltVariante.value.Key === '1') {
          return this.erhoehungSelbstbehalt.value === (this.expertMode ? 'nein' : 'ja') ?
            this.translateService.get('SBVARIANTEVOLLKASKOGENERELLERHOEHUNGSBINFO') : this.translateService.get('SBVARIANTEVOLLKASKOGENERELLINFO');
        }
        if (this.selbstbehaltVariante.value.Key === '2') {
          return this.erhoehungSelbstbehalt.value === (this.expertMode ? 'nein' : 'ja') ?
            this.translateService.get('SBVARIANTEVOLLKASKOEINGESCHRAENKTERHOEHUNGSBINFO') : this.translateService.get('SBVARIANTEVOLLKASKOEINGESCHRAENKTINFO');
        }
      }
      if (this.kaskoVariante.value.Key === '2') {
        if (this.selbstbehaltVariante.value.Key === '1') {
          return this.translateService.get('SBVARIANTETEILKASKOGENERELLINFO');
        }
        if (this.selbstbehaltVariante.value.Key === '2') {
          return this.translateService.get('SBVARIANTETEILKASKOEINGESCHRAENKTINFO');
        }
      }
    }
    return of();
  }

  get vollKaskoInfoTexts() {
    return 'Wenn die gewünschte Kaskovariante nicht angezeigt wird ist sie anfragepflichtig, bitte wenden Sie sich an '.concat(this.expertMode ? 'Ihren Betreuer.' :
      '<a href="mailto:spezielleanfrage@hdi.at">spezielleanfrage@hdi.at.</a>');
  }

  get kaskoIndex() {
    return this.kaskoKfzComponents?.toArray().indexOf(this);
  }

  get kaskosShow() {
    return this.kaskoKfzComponents.filter(x => x.showKaskoversicherung());
  }

  get firstIndex() {
    return this.kaskoKfzComponents?.toArray().indexOf(this.kaskosShow[0]);
  }

  get showHeader() {
    return this.showKaskoversicherung() && this.kaskosShow.length > 1;
  }

  get showFooter() {
    const lastIndex = this.kaskoKfzComponents.toArray().indexOf(this.kaskosShow[this.kaskosShow.length - 1]);
    return !this.complete && this.showHeader && this.kaskoIndex === lastIndex;
  }

  get showKaskoversicherungInfo() {
    return !this.complete && this.kaskoIndex === this.firstIndex;
  }

  get versicherungsart() {
    if (this.showOaemtcInfoForKaskoVariante) {
      return this.kaskoVariante.value?.Value + ' mit generellem SB';
    }
    return this.kaskoVariante.value?.Value + (this.selbstbehaltVariante.value?.Key === '1' ? ' mit generellem SB' :
      this.selbstbehaltVariante.value?.Key === '2' ? ' mit eingeschränktem SB' : '');
  }

  get selbstbehalthoehe() {
    const index = this.showOaemtcInfoForKaskoVariante || this.erhoehungSelbstbehalt?.value === 'ja' ? 1 : 0;
    if (this.complete) {
      return this.kaskoKfzComponent.selbstbehalthoehen.pipe(
        map(selbstbehalthoehen => selbstbehalthoehen[index])
      );
    }
    return this.selbstbehalthoehen.pipe(
      map(selbstbehalthoehen => selbstbehalthoehen[index])
    );
  }

  get maxAlterJahreVollkasko() {
    return this.expertMode && moment(this.beginndatum?.value).isSameOrAfter(moment('2024-04-01')) ? 7 : 4;
  }

  get maxAlterJahreTeilkasko() {
    return this.expertMode && moment(this.beginndatum?.value).isSameOrAfter(moment('2024-04-01')) ? 10 : 7;
  }

  get showOaemtcInfoForKaskoVariante() {
    return this.showOaemtcInfo(this.kaskoVariante.value?.Key);
  }

  get showSelbstbehaltVarianten() {
    return !this.motorrad && this.interesseKaskoversicherung && !this.showOaemtcInfoForKaskoVariante;
  }

  get showSelbstbehaltBlock() {
    return this.showSelbstbehaltVarianten && this.listenpreis.value !== null;
  }

  showOaemtcInfo(kaskoVarianteKey: string): boolean {
    const alterJahre = moment(this.beginndatum?.value).year() - moment(this.risikoKfzComponent?.erstzulassungsdatum.value).year();
    return this.expertMode && moment(this.beginndatum?.value).isSameOrAfter(moment('2024-04-01')) &&
      (kaskoVarianteKey === '1' && alterJahre >= 5 || kaskoVarianteKey === '2' && alterJahre >= 8);
  }

  showKaskoInfoTexts(kaskoVarianteKey: string): object {
    if (this.motorrad) {
      return { 'show': true, 'value': 'moto' };
    } else if (this.showOaemtcInfo(kaskoVarianteKey)) {
      return { 'show': true, 'value': 'oeamtc' };
    }
    return { 'show': false, 'value': '' };
  }


  getKaskoVarianten() {
    const kaskoVarianten = this.risikoKfzComponent?.erstzulassungsdatum && this.risikoKfzComponent?.erstzulassungsdatum.valid &&
      moment(this.risikoKfzComponent?.erstzulassungsdatum.value).year() >= moment(this.beginndatum?.value).year() - this.maxAlterJahreVollkasko ?
      this.kaskoVarianten.asObservable() : this.kaskoVarianten.pipe(map(kaskoVarianten => kaskoVarianten.filter(
        x => (this.motorrad ? x.Key === '4' : x.Key === '2') || x.Key === null))
      );
    return kaskoVarianten;
  }

  setPreselectedSbVariante(): void {
    this.selbstbehaltVarianten.subscribe(data => {
      this.preselectSbVariante = data.find(x => x.Key === '1');
      this.getKasko();
    });
  }

  toggleEditMode(): void {
    toggleEditMode(this.kaskoVariante, this.showKaskoversicherung());
    toggleEditMode(this.listenpreis, this.showListenpreis, this.eurotax || this.complete, !this.eurotax);
    toggleEditMode(this.sonderausstattung, this.showSonderausstattung);
    toggleEditMode(this.fahrzeugZustand, this.showFahrzeugZustand);
    toggleEditMode(this.selbstbehaltVariante, this.showSelbstbehaltVarianten);
    toggleEditMode(this.neuwertentschaedigung, this.showNeuwertentschaedigung);
    toggleEditMode(this.leasingklausel, this.showLeasingklausel);
    toggleEditMode(this.evorsorgepaket, this.showEVorsorgepaket);
    toggleEditMode(this.erhoehungSelbstbehalt, this.showErhoehungSelbstbehalt);
    toggleEditMode(this.leasingFahrzeug, this.showLeasingFahrzeug);
    toggleEditMode(this.bmStufeKasko, this.showBmStufeKasko);
    toggleEditMode(this.vorsteuerabzugsberechtigt, this.showVorsteuerabzugsberechtigt);
    toggleEditMode(this.sonderausstattungDetails, this.showSonderausstattungDetails, false, this.complete);
    toggleEditMode(this.vinkulierungFahrzeug, this.showVinkulierungFahrzeug, false, this.complete);
    toggleEditMode(this.vinkularglaeubiger, this.showVinkularglaeubiger, false, this.complete);
    toggleEditMode(this.leasingMotorrad, this.showLeasingMotorrad, false, this.complete);
    toggleEditMode(this.leasingOrt, this.showLeasingOrt, false, this.complete);
  }

  loadKaskoVarianten(): void {
    if (this.risikoKfzComponent.fahrzeugart.valid && this.beginndatum.valid) {
      this.calculationService.getKaskoVarianten({
        FahrzeugArtId: Number(this.risikoKfzComponent.fahrzeugart.value.Id),
        TarifierungsDatum: this.beginndatum.value
      }).subscribe(
        data => {
          const kaskoVarianten = data.KaskoVarianten;
          this.kaskoVarianten.next(kaskoVarianten);
          const kaskoVariante = kaskoVarianten.find(x => x.Id === this.kaskoVariante.value?.Id);
          if (kaskoVariante) {
            this.kaskoVariante.setValue(kaskoVariante);
          } else {
            this.kaskoVariante.setValue(null);
          }
          if (this.expertMode || this.motorrad) {
            for (const variante of kaskoVarianten) {
              if (variante.Key == null) {
                continue;
              }
              this.calculationService.getSelbstbehalthoehen({
                TarifId: Number(variante.Id),
                FahrzeugArtId: Number(this.risikoKfzComponent.fahrzeugart.value.Id),
                TarifierungsDatum: this.beginndatum.value
              }).subscribe(data => {
                this.selbstbehaltForKaskoInfo.set(variante.Key, Math.max(...data));
              },
                (err: HttpErrorResponse) => {
                  this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.General));
                  console.log(err.message);
                }
              );
            }
          }
        },
        (err: HttpErrorResponse) => {
          this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.General));
          console.log(err.message);
        }
      );
    }
  }

  loadSelbstbehaltVarianten(): void {
    if (!this.motorrad) {
      if (this.kaskoVariante.valid && this.kaskoVariante.value.Id !== null && this.risikoKfzComponent.fahrzeugart.valid &&
        this.beginndatum.valid && (this.eurotax && this.listenpreis.value || this.listenpreis.valid)) {
        this.calculationService.getSelbstbehaltVarianten({
          TarifId: Number(this.kaskoVariante.value.Id),
          FahrzeugArtId: Number(this.risikoKfzComponent.fahrzeugart.value.Id),
          TarifierungsDatum: this.beginndatum.value,
          Listenpreis: Number(this.listenpreis.value)
        }).subscribe(
          data => {
            const sortedSelbstbehaltVarianten = data.SelbstbehaltVarianten.sort((a, b) => Number(a.Key) - Number(b.Key));
            this.selbstbehaltVarianten.next(sortedSelbstbehaltVarianten);
            const selbstbehaltVariante = sortedSelbstbehaltVarianten.find(x => x.Id === this.selbstbehaltVariante.value?.Id);
            if (selbstbehaltVariante) {
              this.selbstbehaltVariante.setValue(selbstbehaltVariante);
            } else {
              this.selbstbehaltVariante.setValue(null);
            }
          },
          (err: HttpErrorResponse) => {
            this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.General));
            console.log(err.message);
          },
          () => {
            toggleEditMode(this.selbstbehaltVariante, this.showSelbstbehaltVarianten);
          }
        );
      }
    }
  }

  loadSelbstbehalthoehen(): void {
    if (this.motorrad) {
      return;
    }
    if (this.beginndatum.valid && this.kaskoVariante.valid && this.kaskoVariante.value.Id !== null && this.risikoKfzComponent.fahrzeugart.valid) {
      this.calculationService.getSelbstbehalthoehen({
        TarifId: Number(this.kaskoVariante.value.Id),
        FahrzeugArtId: Number(this.risikoKfzComponent.fahrzeugart.value.Id),
        TarifierungsDatum: this.beginndatum.value
      }).subscribe(data => {
        this.selbstbehalthoehen.next(data);
      },
        (err: HttpErrorResponse) => {
          this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.General));
          console.log(err.message);
        }
      );
    }
  }

  onValueChanged(): void {
    merge(
      this.risikoKfzComponent.fahrzeugart.valueChanges,
      this.risikoKfzComponent.erstzulassungsdatum.valueChanges,
      this.kaskoVariante.valueChanges,
      this.leasingFahrzeug.valueChanges,
      this.sonderausstattung.valueChanges
    ).subscribe(() => {
      this.toggleEditMode();
    });
    merge(
      this.risikoKfzComponent.fahrzeugart.valueChanges,
      this.beginndatum.valueChanges
    ).subscribe(() => {
      this.loadKaskoVarianten();
    });
    this.kaskoVariante.valueChanges.subscribe(() => {
      this.loadSelbstbehaltVarianten();
    });
    merge(
      this.risikoKfzComponent.fahrzeugart.valueChanges,
      this.kaskoVariante.valueChanges,
      this.beginndatum.valueChanges
    ).subscribe(() => {
      this.loadSelbstbehalthoehen();
    });
    this.listenpreis.valueChanges.subscribe(() => {
      this.loadSelbstbehaltVarianten();
      if (this.listenpreis.valid) {
        this.parameterChangedEvent.emit();
      }
    });
    this.risikoKfzComponent.erstzulassungsdatum.valueChanges.subscribe(() => {
      if (this.kaskoVariantenSubscription) {
        this.kaskoVariantenSubscription.unsubscribe();
      }
      this.kaskoVariantenSubscription = this.getKaskoVarianten().subscribe(res => {
        if (res.filter(x => x.Key !== null).length === 1) {
          this.kaskoVariante.setValue(res[res.length - 1]);
          this.kaskoVarianteDisabled = true;
        } else {
          this.kaskoVarianteDisabled = false;
        }
      });
    });
    this.sonderausstattung.valueChanges.subscribe(() => {
      if (this.sonderausstattung.value > 4000) {
        this.sonderausstattungDetails.addValidators(Validators.required);
      } else {
        this.sonderausstattungDetails.removeValidators(Validators.required);
      }
    });
    merge(
      this.kaskoVariante.valueChanges,
      this.sonderausstattung.valueChanges,
      this.fahrzeugZustand.valueChanges,
      this.selbstbehaltVariante.valueChanges,
      this.neuwertentschaedigung.valueChanges,
      this.leasingFahrzeug.valueChanges,
      this.leasingklausel.valueChanges,
      this.evorsorgepaket.valueChanges,
      this.erhoehungSelbstbehalt.valueChanges,
      this.bmStufeKasko.valueChanges,
      this.vorsteuerabzugsberechtigt.valueChanges
    ).subscribe(() => {
      if (this.sonderausstattung.valid) {
        this.parameterChangedEvent.emit();
      }
    });

    this.leasingFahrzeug.valueChanges.subscribe((data) => {
      const options = { emitEvent: false };
      if (data === 'ja') {
        this.vinkulierungFahrzeug.setValue('ja', options);
      } else {
        this.vinkulierungFahrzeug.setValue('nein', options);
      }
    });
    merge(
      this.risikoKfzComponent.erstzulassungsdatum.valueChanges,
      this.beginndatum.valueChanges,
      this.kaskoVariante.valueChanges
    ).subscribe(() => {
      this.toggleEditMode();
      if (this.showOaemtcInfoForKaskoVariante) {
        this.setPreselectedSbVariante();
      }
      this.parameterChangedEvent.emit();
    });
    merge(
      this.kaskoVariante.valueChanges,
      this.leasingFahrzeug.valueChanges
    ).subscribe(() => {
      if (this.showLeasingklausel) {
        this.leasingklausel.setValue('ja');
      }
    });
  }

  kaskoAltesBaujahr(kaskoVarianteKey: string): boolean {
    const alterJahre = moment(this.beginndatum?.value).year() - moment(this.risikoKfzComponent?.erstzulassungsdatum.value).year();
    return (kaskoVarianteKey === '1' || kaskoVarianteKey === '3') && alterJahre >= 6 ||
      (kaskoVarianteKey === '2' || kaskoVarianteKey === '4') && alterJahre >= 8 ? true : false;
  }

  getKaskoInfoText(kaskoVarianteKey: string): string {
    if (!this.beginndatum.valid) {
      return '';
    }
    switch (kaskoVarianteKey) {
      case '1':
        return 'Voraussetzung für die Kaskodeckung ist eine schadenfreie ÖAMTC Fahrzeugbesichtigung sowie eine positive Annahmeprüfung. '
          + `Die Variante Top-Vollkasko mit generellem SB EUR ${this.selbstbehaltForKaskoInfo.get(kaskoVarianteKey)},- gilt als vorgegeben.`;
      case '2':
        return 'Voraussetzung für die Kaskodeckung ist eine schadenfreie ÖAMTC Fahrzeugbesichtigung sowie eine positive Annahmeprüfung. '
          + `Die Variante Teilkasko mit generellem SB EUR ${this.selbstbehaltForKaskoInfo.get(kaskoVarianteKey)},- gilt als vorgegeben.`;
      case '3':
        return `Selbstbehalt 5% des Schadens, mindestens € ${this.selbstbehaltForKaskoInfo.get(kaskoVarianteKey)},-`;
      case '4':
        return 'Ohne Selbstbehalt';
      default:
        return '';
    }
  }

  onStatusChanged(): void {
    this.kaskoVariante.statusChanges.subscribe(() => {
      this.kaskoVarianteDisabled = this.kaskoVariante.disabled;
    });
  }

  goToLink(url: string) {
    window.open(url);
  }

  setEurotaxEditMode() {
    const options = { emitEvent: false };
    if (this.showListenpreis) {
      this.listenpreis.disable(options);
    }
  }

  setEurotax(result: IEuroTaxResult) {
    this.eurotax = true;

    if (this.eurotaxListenpreis) {
      this.eurotaxListenpreis.unsubscribe();
    }

    this.eurotaxListenpreis = this.kaskoVariante.valueChanges.subscribe(() => {
      if (this.showListenpreis) {
        this.listenpreis.setValue(result.preisInkl);
      }
    });
    if (this.showListenpreis) {
      this.listenpreis.setValue(result.preisInkl);
    }
  }

  resetEurotax() {
    this.eurotax = false;

    if (this.eurotaxListenpreis) {
      this.eurotaxListenpreis.unsubscribe();
    }

    if (this.showListenpreis) {
      this.listenpreis.setValue(null);
      this.listenpreis.enable();
    }
  }

  setKasko(risiko: IKfzRisiko) {
    return new Promise<void>(resolve => {
      const kasko = risiko.Kasko;
      if (kasko) {
        this.kaskoVarianten.pipe(first()).toPromise().then((kaskoVarianten) => {
          this.eurotax = risiko.Eurotax;
          this.kaskoVariante.setValue(kaskoVarianten.find(x => x.Id === kasko.KaskoTarifId.toString()));

          this.listenpreis.setValue(kasko.ListenPreisBerechnungsWert);
          this.sonderausstattung.setValue(kasko.SonderausstattungPreis);

          this.neuwertentschaedigung.setValue(kasko.Neuwertentschaedigung != null ?
            kasko.Neuwertentschaedigung === true ? 'ja' : 'nein' : null);
          this.evorsorgepaket.setValue(kasko.EVorsorgepaket != null ?
            kasko.EVorsorgepaket === true ? 'ja' : 'nein' : null);
          this.erhoehungSelbstbehalt.setValue(kasko.ErhoehungSb != null ? kasko.ErhoehungSb === true ? 'ja' : 'nein' : null);
          this.leasingFahrzeug.setValue(kasko.LeasingFahrzeug != null ?
            kasko.LeasingFahrzeug === true ? 'ja' : 'nein' : null);

          this.fahrzeugZustand.setValue(kasko.Neufahrzeug != null ? kasko.Neufahrzeug === true ? 'ja' : 'nein' : null);
          this.selbstbehaltVarianten.pipe(first()).toPromise().then((selbstbehaltVarianten) => {
            this.selbstbehaltVariante.setValue(selbstbehaltVarianten.find(x => x.Id === kasko.SelbstbehaltVarianteId.toString()));
          });
          if (kasko.VertragBmStufeId) {
            this.bmStufenKasko.pipe(first()).toPromise().then((bmStufenKasko) => {
              this.bmStufeKasko.setValue(bmStufenKasko.find(x => x.Id === kasko.VertragBmStufeId.toString()));
            });
          }
          this.sonderausstattungDetails.setValue(kasko.SonderausstattungDetails);
          this.vinkulierungFahrzeug.setValue(kasko.Vinkulierung != null ? kasko.Vinkulierung === true ? 'ja' : 'nein' : null);
          this.vinkularglaeubiger.setValue(kasko.Vinkularglaeubiger);
          this.leasingOrt.setValue(kasko.LeasingOrt);
          this.vorsteuerabzugsberechtigt.setValue(kasko.Vorsteuerabzugsberechtigt != null ?
            kasko.Vorsteuerabzugsberechtigt === true ? 'ja' : 'nein' : null);
          if (this.eurotax) {
            this.setEurotaxEditMode();
          }
          resolve();
        });
      } else {
        this.kaskoVarianten.pipe(first()).toPromise().then((kaskoVarianten) => {
          this.kaskoVariante.setValue(kaskoVarianten.find(x => x.Id === null));
          resolve();
        });
      }
    });
  }

  getKasko(): IKfzKasko {
    return {
      KaskoTarifId: Number(this.kaskoVariante.value.Id),
      ListenPreisBerechnungsWert: this.listenpreis.value,
      SonderausstattungPreis: this.sonderausstattung.value,
      Neufahrzeug: this.fahrzeugZustand.value != null ? this.fahrzeugZustand.value === 'ja' : null,
      SelbstbehaltVarianteId: this.showOaemtcInfo(this.kaskoVariante.value?.Key) ?
        this.preselectSbVariante?.Id : this.selbstbehaltVariante.value ?
          Number(this.selbstbehaltVariante.value.Id) : null,
      Neuwertentschaedigung: this.neuwertentschaedigung.value != null ?
        this.neuwertentschaedigung.value === 'ja' : null,
      Leasingklausel: this.leasingklausel.value != null ? this.leasingklausel.value === 'ja' : null,
      EVorsorgepaket: this.evorsorgepaket.value != null ? this.evorsorgepaket.value === 'ja' : null,
      ErhoehungSb: this.showOaemtcInfo(this.kaskoVariante.value?.Key) ? true : this.erhoehungSelbstbehalt.value != null ? this.erhoehungSelbstbehalt.value === 'ja' : null,
      LeasingFahrzeug: this.leasingFahrzeug.value != null ? this.leasingFahrzeug.value === 'ja' :
        this.leasingMotorrad.value != null ? this.leasingMotorrad.value === 'ja' : null,
      SonderausstattungDetails: this.sonderausstattungDetails.value ?
        this.sonderausstattungDetails.value : null,
      Vinkulierung: this.vinkulierungFahrzeug.value != null ? this.vinkulierungFahrzeug.value === 'ja' : null,
      Vinkularglaeubiger: this.vinkularglaeubiger.value ?
        this.vinkularglaeubiger.value : null,
      LeasingOrt: this.leasingOrt.value ?
        this.leasingOrt.value : null,
      Vorsteuerabzugsberechtigt: this.vorsteuerabzugsberechtigt.value != null ?
        this.vorsteuerabzugsberechtigt.value === 'ja' : null,
    } as IKfzKasko;
  }
}
